import React from 'react';
import './SettingCard.css';
import {
	SettingNote,
	SettingNoteType,
	SETTING_NOTE_MESSAGES,
} from './SettingStatus/SettingStatus';
import { SettingCardContent } from './SettingCardContent/SettingCardContent';
import { DeprecatedSettingIcon } from '../../../Icons/DeprecatedSettingIcon/DeprecatedSettingIcon';
import { getLinkedUrl } from '../../../config/LinkedUrlsConfig';

export interface SettingCardProps {
	/** The title of the setting */
	title: string | React.ReactNode;

	/** Optional description text for the setting */
	description?: string | React.ReactNode;

	/** Unique identifier for the setting */
	settingId: string;

	/** Content to render inside the card */
	content?: React.ReactNode;

	/** Additional CSS class name */
	className?: string;

	/** Whether to show status message on value change */
	showStatusOnValueChange?: boolean;

	/** Status message to display when showStatusOnValueChange is true */
	statusMessage?: string;

	/** Whether the setting is disabled */
	disabled?: boolean;

	/** Whether to show a separator between header and content */
	showContentSeparator?: boolean;

	/** Optional URL for "Learn more" link appended to description */
	learnMoreUrl?: string;

	/** Optional custom text for the learn more link (defaults to "Learn more") */
	learnMoreText?: string;

	/** Whether to show a help icon next to the title */
	showHelpIcon?: boolean;

	/** Help text to display when help icon is clicked/hovered */
	helpText?: string;

	/** Whether the setting is deprecated */
	deprecated?: boolean;

	/** Show admin-managed note at the card level without disabling the whole card.
	 *  Use when a sub-setting inside a group is disabled while the parent card is still active. */
	showAdminNote?: boolean;
}

export const SettingCard: React.FC<SettingCardProps> = ({
	title,
	description,
	settingId,
	content,
	className = '',
	showStatusOnValueChange = false,
	statusMessage,
	disabled = false,
	showContentSeparator = false,
	learnMoreUrl,
	learnMoreText = 'Learn more',
	deprecated = false,
	showAdminNote = false,
}) => {
	const resolvedLearnMoreUrl = learnMoreUrl
		? getLinkedUrl(settingId, learnMoreUrl)
		: undefined;

	const renderDescription = () => {
		if (!description) return null;

		if (resolvedLearnMoreUrl) {
			// Render description with inline learn more link
			if (typeof description === 'string') {
				return (
					<div className="setting-card-description">
						<p>
							{description}{' '}
							<a
								href={resolvedLearnMoreUrl}
								target="_blank"
								rel="noopener noreferrer"
								data-testid="learn-more-link"
								className="setting-card-learn-more"
							>
								{learnMoreText}
							</a>
						</p>
					</div>
				);
			} else {
				// For custom description components, append link after
				return (
					<div className="setting-card-description">
						{description}{' '}
						<a
							href={resolvedLearnMoreUrl}
							target="_blank"
							rel="noopener noreferrer"
							data-testid="learn-more-link"
							className="setting-card-learn-more"
						>
							{learnMoreText}
						</a>
					</div>
				);
			}
		}

		const descriptionContent =
			typeof description === 'string' ? <p>{description}</p> : description;
		return <div className="setting-card-description">{descriptionContent}</div>;
	};

	return (
		<div
			className={`setting-card ${className} ${disabled ? 'setting-card-disabled' : ''} ${
				showContentSeparator ? 'with-content-separator' : ''
			}`.trim()}
			id={settingId}
		>
			<div className="setting-card-header">
				<div className="setting-card-header-content">
					{typeof title === 'string' ? <h3>{title}</h3> : title}
				</div>
				{deprecated && (
					<DeprecatedSettingIcon
						width={20}
						height={20}
						className="setting-card-deprecated-icon"
					/>
				)}
			</div>
			{renderDescription()}
			{content && <SettingCardContent>{content}</SettingCardContent>}
			{(disabled || showAdminNote || (showStatusOnValueChange && statusMessage)) && (
				<SettingNote
					type={
						disabled || showAdminNote ? SettingNoteType.Info : SettingNoteType.Warning
					}
					message={
						disabled || showAdminNote
							? SETTING_NOTE_MESSAGES.ADMIN_MANAGED
							: statusMessage
					}
				/>
			)}
		</div>
	);
};
